/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Suspense, lazy, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Modal } from 'react-bootstrap';
import { PageTitle } from 'src/_metronic/layout/core';
import { useAuth } from 'src/app/modules/auth/core/Auth';
import { fetchAllDashboardData, AllDashboardData } from './services/dashboardApi';
import AnimatedStatsCard from './components/AnimatedStatsCard';
import './dashboard.css';
import {
  StatsCardSkeleton,
  ChartSkeleton,
  TableSkeleton,
  SmallTableSkeleton,
} from './components/SkeletonLoaders';
import { LibraryRecordsTable } from './components';

// Lazy load all components for better performance
const TurnAroundTimeChart = lazy(() => import('./components/TurnAroundTimeChart'));
const DepartmentDistributionChart = lazy(() => import('./components/DepartmentDistributionChart'));
const ProcessTable = lazy(() => import('./components/ProcessTable'));
const DepartmentPerformanceTable = lazy(() => import('./components/DepartmentPerformanceTable'));
const DepartmentPersonnelTable = lazy(() => import('./components/DepartmentPersonnelTable'));

const DashboardPage: React.FC = () => {
  const {currentUser} = useAuth()
  const [dashboardData, setDashboardData] = useState<AllDashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showStorageLimitModal, setShowStorageLimitModal] = useState(true);
  const [showStorageWarningModal, setShowStorageWarningModal] = useState(true);

  const storageLimitStatus = currentUser?.licenseValidationStatus?.storageSizeStatus?.licenseStorageSizeStatusEnum
  
  
  const storageUtilizationPct = currentUser?.licenseValidationStatus?.storageSizeStatus?.storageUtilizationPct ?? 0
  const storageAlarmThreshold = 80

  const showStorageLimitAlert = storageLimitStatus === 'EXCEED_LIMIT'
  const showStorageWarningAlert = storageUtilizationPct >= storageAlarmThreshold && storageLimitStatus !== 'EXCEED_LIMIT'

  const handleCloseStorageLimitModal = () => {
    setShowStorageLimitModal(false)
  }

  const handleCloseStorageWarningModal = () => {
    setShowStorageWarningModal(false)
  }

  useEffect(() => {
    const loadDashboardData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchAllDashboardData();
        setDashboardData(data);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <>
      {/* Storage Limit Alert Modal (EXCEED_LIMIT) */}
      {showStorageLimitAlert && (
        <Modal 
          show={showStorageLimitModal} 
          onHide={handleCloseStorageLimitModal}
          backdrop="static"
          keyboard={false}
          centered
        >
          <Modal.Header closeButton style={{border: 'none'}} />
          <Modal.Body style={{padding: '40px 30px'}}>
            <div style={{display: 'flex', alignItems: 'flex-start', gap: '25px'}}>
              <div style={{display: 'flex', flexShrink: 0}}>
                <i className="bi bi-exclamation-triangle-fill" style={{fontSize: '3rem', color: '#dc3545'}}></i>
              </div>
              <div style={{flex: 1}}>
                <p style={{fontSize: '0.95rem', lineHeight: '1.6', marginBottom: 0}}>
                  Your storage utilization has reached <strong>limit</strong>. You can add new and edit data. But you are unable upload new documents. To remove the storage limit, you need to top up your storage subscription.
                </p>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer style={{borderTop: 'none', paddingTop: 0}}>
            <button 
              type="button" 
              className="btn btn-secondary btn-sm px-4" 
              onClick={handleCloseStorageLimitModal}
            >
              Close
            </button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Storage Warning Alert Modal (Percentage >= 80%) */}
      {showStorageWarningAlert && (
        <Modal 
          show={showStorageWarningModal} 
          onHide={handleCloseStorageWarningModal}
          backdrop="static"
          keyboard={false}
          centered
        >
          <Modal.Header closeButton style={{border: 'none'}} />
          <Modal.Body style={{padding: '40px 30px'}}>
            <div style={{display: 'flex', alignItems: 'flex-start', gap: '25px'}}>
              <div style={{display: 'flex', flexShrink: 0}}>
                <i className="bi bi-exclamation-triangle-fill" style={{fontSize: '3rem', color: '#dc3545'}}></i>
              </div>
              <div style={{flex: 1}}>
                <p style={{fontSize: '0.95rem', lineHeight: '1.6', marginBottom: 0}}>
                  Your storage utilization has reached <strong>{storageUtilizationPct}%</strong>. To avoid upload new documents interruption, kindly top up your storage.
                </p>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer style={{borderTop: 'none', paddingTop: 0}}>
            <button 
              type="button" 
              className="btn btn-secondary btn-sm px-4" 
              onClick={handleCloseStorageWarningModal}
            >
              Close
            </button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Top Stats Cards */}
      <div className="row g-5 g-xl-8 mb-5 mb-xl-8">
        {isLoading ? (
          // Show skeletons while loading
          <>
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="col-xl col-md-6 col-sm-6">
                <StatsCardSkeleton />
              </div>
            ))}
          </>
        ) : (
          // Show animated cards when loaded
          <>
            {dashboardData?.statsCards.map((card, index) => (
              <div key={index} className="col-xl col-md-6 col-sm-6">
                <AnimatedStatsCard data={card} />
              </div>
            ))}
          </>
        )}
      </div>

      {/* Main Content Row - Charts and Department Personnel */}
      <div className="row g-5 g-xl-8 mb-5 mb-xl-8">
        <div className="col-xl-8">
          {/* Charts Row */}
          <div className="row g-5 g-xl-8 mb-5 mb-xl-8">
            {/* Turn Around Time Performance Chart */}
            <div className="col-xl-7">
              <Suspense fallback={<ChartSkeleton />}>
                {dashboardData ? (
                  <TurnAroundTimeChart 
                    data={dashboardData.turnAroundTimeData}
                    monthlyPerformance={dashboardData.monthlyPerformance}
                  />
                ) : (
                  <ChartSkeleton />
                )}
              </Suspense>
            </div>

            {/* Department Distribution Donut Chart */}
            <div className="col-xl-5">
              <Suspense fallback={<ChartSkeleton />}>
                {dashboardData ? (
                  <DepartmentDistributionChart 
                    data={dashboardData.departmentDistribution}
                    priorityTasks={dashboardData.priorityTasks}
                  />
                ) : (
                  <ChartSkeleton />
                )}
              </Suspense>
                
            </div>
          </div>

         
        </div>

        {/* Department Personnel Table - Spans 2 rows */}
        <div className="col-xl-4">
          <Suspense fallback={<TableSkeleton rows={8} />}>
            <DepartmentPersonnelTable />
          </Suspense>
        </div>
      </div>

      {/* Library Records and Process Tables Row */}
      <div className="row g-5 g-xl-8 mb-5 mb-xl-8">
        {/* Library Records Table */}
         <div className="col-xl-3">
          <Suspense fallback={<TableSkeleton rows={3} />}>
            <LibraryRecordsTable />
          </Suspense>
        </div>

        {/* Process Table */}
        <div className="col-xl-6">
          <Suspense fallback={<TableSkeleton rows={3} />}>
            {dashboardData ? (
              <ProcessTable data={dashboardData.processData} />
            ) : (
              <TableSkeleton rows={3} />
            )}
          </Suspense>
        </div>

           <div className="col-xl-3">
          <Suspense fallback={<SmallTableSkeleton />}>
            {dashboardData ? (
              <DepartmentPerformanceTable data={dashboardData.departmentPerformance} />
            ) : (
              <SmallTableSkeleton />
            )}
          </Suspense>
        </div>
      </div>

      
    </>
  );
};

const DashboardWrapper: React.FC = () => {
  const intl = useIntl();
  return (
    <>
      <PageTitle>
        {intl.formatMessage({ id: 'MENU.DASHBOARD' })}
      </PageTitle>
      <DashboardPage />
    </>
  );
};

export { DashboardWrapper };
